import React, { useState, useRef, useEffect } from 'react';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { X, ChevronDown } from 'lucide-react';
import { InfoTooltip } from '../components/ui/info-tooltip';

const TagSelector = ({ 
  title, 
  tooltipText, 
  selectedTags = [], 
  onTagsChange,
  tableName,
  className,
  availableTags 
}) => {
  const [newTagInput, setNewTagInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestionsRef = useRef(null);
  const timeoutRef = useRef(null);
  const isCompatibilityTags = tableName === 'compatibility_tags';
  
  const suggestions = availableTags
  .filter(tag => {
    // Check if tag name matches search input
    const matchesSearch = tag.name.toLowerCase().includes(newTagInput.toLowerCase());
    
    // Check if tag is not already selected in any category
    const isNotSelected = !selectedTags.some(selectedTag => {
      // Try to parse JSON only if it starts with '{'
      let selectedTagName = selectedTag.name;
      if (typeof selectedTag.name === 'string' && selectedTag.name.startsWith('{')) {
        try {
          selectedTagName = JSON.parse(selectedTag.name).name;
        } catch (e) {
          // If JSON parsing fails, use the original name
          selectedTagName = selectedTag.name;
        }
      }
      
      return selectedTag.id === tag.id || selectedTagName === tag.name;
    });
    
    return matchesSearch && isNotSelected;
  });
  
  const handleTagClick = (tag) => {
    const tagId = tag.id;
    if (!tagId) {
      console.warn('Tag missing ID:', tag);
      return;
    }
    
    const isSelected = selectedTags.some(t => t.id === tagId);
    
    if (isSelected) {
      onTagsChange({
        existing: selectedTags.filter(t => t.id !== tagId),
        new: []
      });
    } else {
      onTagsChange({
        existing: [...selectedTags, tag],
        new: []
      });
    }
    
    setShowSuggestions(false);
    setNewTagInput('');
  };

  const handleBlur = () => {
    // Use timeout to allow click events on suggestions to fire first
    timeoutRef.current = setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  useEffect(() => {
    // Cleanup timeout on component unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div className={className}>
      <div className="flex items-center gap-2 mb-3">
        <Label className="text-sm font-medium">{title}</Label>
        {tooltipText && <InfoTooltip text={tooltipText} />}
      </div>
  
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2 min-h-[2.5rem] p-3 bg-gray-50 border border-gray-200 rounded-lg">
          {selectedTags.length > 0 ? (
            selectedTags.map((tag) => (
              <Button
                key={tag.id}
                variant="secondary"
                size="sm"
                className="bg-white hover:bg-gray-100 text-gray-700 border border-gray-200"
                onClick={() => handleTagClick(tag)}
              >
                <span className="flex items-center gap-1">
                  {typeof tag.name === 'string' && tag.name.startsWith('{') 
                    ? JSON.parse(tag.name).name 
                    : tag.name}
                  {isCompatibilityTags && tag.category && (
                    <span className="text-xs px-1 py-0.5 bg-gray-100 text-gray-600 rounded">
                      {tag.category}
                    </span>
                  )}
                </span>
                <X className="w-3 h-3 ml-2 text-gray-400" />
              </Button>
            ))
          ) : (
            <span className="text-sm text-gray-500 p-1">No tags selected</span>
          )}
        </div>
  
        <div className="relative" ref={suggestionsRef}>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Input
                placeholder="Cerca tra i tag esistenti..."
                value={newTagInput}
                onChange={(e) => {
                  setNewTagInput(e.target.value);
                  setShowSuggestions(true);
                }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={handleBlur}
                className="pr-8"
              />
              <ChevronDown 
                className={`absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 transition-transform ${showSuggestions ? 'rotate-180' : ''}`}
                onClick={() => setShowSuggestions(!showSuggestions)}
              />
            </div>
          </div>
  
          {showSuggestions && suggestions.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg">
              <div className="max-h-60 overflow-y-scroll">
                {suggestions.map((tag) => (
                  <button
                    key={tag.id}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 focus:bg-gray-50 focus:outline-none text-gray-700"
                    onClick={() => handleTagClick(tag)}
                  >
                    <span className="flex items-center justify-between">
                      {tag.name}
                      {isCompatibilityTags && tag.category && (
                        <span className="text-xs px-2 py-0.5 bg-gray-50 text-gray-600 rounded">
                          {tag.category}
                        </span>
                      )}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export { TagSelector };